<template>
    <div class="step-wrap">
        <!-- Header -->
        <div class="step-header shrink-0">
            <p class="step-eyebrow">Last big step</p>
            <h1 class="step-h1">Tell the story</h1>
            <p class="step-lead">The moment, the tension, the payoff.</p>
        </div>

        <div class="step-body">
            <!-- Template pills -->
            <div class="templates-block">
                <div class="templates-header">
                    <p class="section-label">Need a starting point?</p>
                    <button class="shuffle-btn" @click="rotateTemplates"><RefreshCw style="width:14px;height:14px" /> Shuffle</button>
                </div>
                <div class="template-pills hide-scrollbar">
                    <button
                        v-for="t in visibleTemplates"
                        :key="t.id"
                        class="template-pill"
                        @click="handleUseTemplate(t)"
                    >{{ t.label }}</button>
                </div>
                <!-- Replace confirm -->
                <Transition name="fade-height">
                    <div v-if="pendingTemplate" class="replace-confirm">
                        <p class="replace-text">Replace your text with <strong>"{{ pendingTemplate.label }}"</strong>?</p>
                        <button class="replace-yes" @click="confirmReplace">Replace</button>
                        <button class="replace-no" @click="pendingTemplate = null">Keep mine</button>
                    </div>
                </Transition>
            </div>

            <!-- Textarea -->
            <textarea
                :value="setup"
                maxlength="280"
                :placeholder='`e.g. "She\u2019s the new personal trainer. Her most demanding client always gets what he wants."`'
                class="synopsis-textarea"
                @input="$emit('update:setup', $event.target.value.slice(0, 280))"
                @focus="$emit('update:inputFocused', true)"
                @blur="$emit('update:inputFocused', false)"
            />
            <div class="char-row">
                <span :style="{ color: setup.length > 250 ? 'rgba(251,191,36,0.7)' : 'rgba(255,255,255,0.3)' }" class="char-count">
                    {{ setup.length }}/280
                </span>
            </div>
        </div>

        <!-- Options strip -->
        <div class="options-strip shrink-0">
            <!-- Tag pill -->
            <div v-if="tags.length > 0" class="tag-count-pill">
                <button class="tag-count-btn" @click="showTagSheet = true"><Hash style="width:10px;height:10px" /> {{ tags.length }} tag{{ tags.length !== 1 ? 's' : '' }}</button>
                <button class="tag-clear-btn" @click="clearAllTags" aria-label="Clear all tags"><X style="width:12px;height:12px" /></button>
            </div>
            <!-- Undo -->
            <Transition name="fade">
                <button v-if="clearedTags" class="undo-btn" @click="handleUndoClear">
                    <Undo2 style="width:14px;height:14px" /> Undo · restore {{ clearedTags.length }} tag{{ clearedTags.length !== 1 ? 's' : '' }}
                </button>
            </Transition>
            <button class="add-tags-btn" :disabled="tags.length >= 10" @click="showTagSheet = true">
                <Hash style="width:10px;height:10px" /> {{ tags.length === 0 ? 'Add tags' : 'Edit tags' }}
            </button>
            <button class="notes-btn" :class="{ 'notes-active': directorsNotes }" @click="showNotesSheet = true">
                <FileText style="width:14px;height:14px" /> {{ directorsNotes ? 'Notes' : 'Add notes' }}<Check v-if="directorsNotes" style="width:12px;height:12px" />
            </button>
        </div>

        <!-- CTA -->
        <div class="step-footer shrink-0">
            <Transition name="fade">
                <p v-if="error" class="error-msg">Write a setup — even one sentence works.</p>
            </Transition>
            <button class="cta-btn" @click="handleNext">Review &amp; launch</button>
        </div>

        <!-- ── Tag Sheet ── -->
        <Teleport to="body">
            <Transition name="backdrop">
                <div v-if="showTagSheet" class="sheet-backdrop" @click.self="showTagSheet = false">
                    <Transition name="sheet-up">
                        <div v-if="showTagSheet" class="bottom-sheet">
                            <div class="drag-handle" />
                            <div class="sheet-header">
                                <div>
                                    <h2 class="sheet-title">Tags</h2>
                                    <p class="sheet-sub">{{ tags.length }}/10 · Help performers find your pitch</p>
                                </div>
                                <button class="sheet-done" @click="showTagSheet = false">Done</button>
                            </div>
                            <div v-if="tags.length > 0" class="tag-chips">
                                <div v-for="tag in tags" :key="tag" class="tag-chip">
                                    <span class="tag-chip-hash"><Hash style="width:10px;height:10px" /></span>
                                    <span class="tag-chip-text">{{ tag }}</span>
                                    <button class="tag-chip-remove" @click="$emit('toggleTag', tag)"><X style="width:12px;height:12px" /></button>
                                </div>
                            </div>
                            <div v-if="suggestedTags.length > 0 && tags.length === 0 && !tagSearch.trim()" class="suggested-tags">
                                <p class="section-label">Suggested for you</p>
                                <div class="suggested-row">
                                    <button
                                        v-for="tag in suggestedTags"
                                        :key="tag"
                                        class="suggested-pill"
                                        :disabled="tags.length >= 10"
                                        @click="addTag(tag)"
                                    >+ {{ tag }}</button>
                                </div>
                            </div>
                            <div class="tag-search-wrap">
                                <input
                                    ref="tagInputEl"
                                    v-model="tagSearch"
                                    type="text"
                                    placeholder="Search or create a tag…"
                                    class="tag-search-input"
                                    :disabled="tags.length >= 10"
                                    @keydown.enter="onTagEnter"
                                />
                            </div>
                            <div class="tag-results hide-scrollbar">
                                <p v-if="!tagSearch.trim()" class="tag-hint">Start typing to search 100+ tags, or create your own.</p>
                                <p v-else-if="filteredTags.length === 0 && exactTagMatch" class="tag-hint">Already added or no matches.</p>
                                <button v-for="tag in filteredTags" :key="tag" class="tag-result-btn" @click="addTag(tag)">
                                    <Hash style="width:10px;height:10px" /> {{ tag }}
                                </button>
                                <button
                                    v-if="!exactTagMatch && tagSearch.trim()"
                                    class="tag-custom-btn"
                                    @click="addCustomTag"
                                >+ Add "{{ tagSearch.trim().slice(0, 30) }}" as new tag</button>
                            </div>
                        </div>
                    </Transition>
                </div>
            </Transition>
        </Teleport>

        <!-- ── Director's Notes Sheet ── -->
        <Teleport to="body">
            <Transition name="backdrop">
                <div v-if="showNotesSheet" class="sheet-backdrop" @click.self="showNotesSheet = false">
                    <Transition name="sheet-up">
                        <div v-if="showNotesSheet" class="bottom-sheet">
                            <div class="drag-handle" />
                            <div class="sheet-header">
                                <div>
                                    <h2 class="sheet-title">Director's notes</h2>
                                    <p class="sheet-sub">Optional — vibe, pacing, a must-have moment</p>
                                </div>
                                <button class="sheet-done" @click="showNotesSheet = false">Done</button>
                            </div>
                            <div class="notes-body">
                <textarea
                    :value="directorsNotes"
                    rows="6"
                    placeholder="Anything you'd tell the director — vibe, pacing, a must-have moment…"
                    class="notes-textarea"
                    autofocus
                    @input="$emit('update:directorsNotes', $event.target.value)"
                    @focus="$emit('update:inputFocused', true)"
                    @blur="$emit('update:inputFocused', false)"
                />
                                <button
                                    v-if="directorsNotes"
                                    class="clear-notes"
                                    @click="() => { $emit('update:directorsNotes', ''); showNotesSheet = false }"
                                >Clear notes</button>
                            </div>
                        </div>
                    </Transition>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from 'vue'
import { RefreshCw, X, Undo2, Hash, FileText, Check } from 'lucide-vue-next'

const SETTINGS_DATA = [
    { id:'bedroom', name:'Bedroom' }, { id:'office', name:'Office' }, { id:'pool', name:'Pool / Outdoors' },
    { id:'hotel', name:'Hotel Room' }, { id:'gym', name:'Gym' }, { id:'kitchen', name:'Kitchen' },
    { id:'living', name:'Living Room' }, { id:'classroom', name:'Classroom' }, { id:'massage', name:'Massage Studio' }
]

const TEMPLATES = [
    { id:'trainer',  label:'The Trainer',   settingPrefs:['gym','pool','massage'],
        body:{ oneCast:'{cast1} is the new personal trainer. Her most demanding client always gets exactly what he asks for. Today\'s session runs long.', zeroCast:'She\'s the new personal trainer. Her most demanding client always gets exactly what he asks for.' }},
    { id:'boss',     label:'The Boss',      settingPrefs:['office','hotel'],
        body:{ oneCast:'{cast1} is the boss. The employee she\'s been eyeing for months is the last one still in the office.', zeroCast:'She\'s the boss. The employee she\'s been eyeing for months is the last one still in the office.' }},
    { id:'tutor',    label:'The Tutor',     settingPrefs:['classroom','living','bedroom'],
        body:{ oneCast:'{cast1} is the private tutor. Her student has been staring instead of studying for weeks.', zeroCast:'She\'s the private tutor. Her student has been staring instead of studying for weeks.' }},
    { id:'landlord', label:'The Landlord',  settingPrefs:['hotel','living','bedroom'],
        body:{ zeroCast:'She\'s the landlord. Her tenant is months behind on rent. She shows up and makes it very clear there\'s one way to settle the balance.' }},
    { id:'massage',  label:'The Massage',   settingPrefs:['massage','hotel'],
        body:{ zeroCast:'She booked the deep-tissue massage for her husband\'s birthday. When he doesn\'t show, she asks if she can take his slot instead.' }},
    { id:'hotmom',   label:'The Hot Mom',   settingPrefs:['living','kitchen','bedroom'],
        body:{ zeroCast:'Her son\'s best friend is still hanging around the house, making excuses not to leave. She\'s noticed the way he looks at her for months.' }},
]

const props = defineProps({
    setup: String, directorsNotes: String, tags: Array, suggestedTags: Array,
    selectedCast: Array, customCast: Array, settings: Array, allTags: Array,
})
const emit = defineEmits(['update:setup', 'update:directorsNotes', 'toggleTag', 'next', 'update:inputFocused'])

const error          = ref(false)
const showTagSheet   = ref(false)
const showNotesSheet = ref(false)
const tagSearch      = ref('')
const tagInputEl     = ref(null)
const templateOffset = ref(0)
const pendingTemplate = ref(null)
const clearedTags    = ref(null)
let clearedTimer = null

watch(() => props.setup, v => { if (v.trim().length > 0) error.value = false })
watch(showTagSheet, v => { if (v) { document.body.style.overflow = 'hidden'; nextTick(() => tagInputEl.value?.focus()) } else document.body.style.overflow = '' })
watch(showNotesSheet, v => { document.body.style.overflow = v ? 'hidden' : '' })
onUnmounted(() => { document.body.style.overflow = ''; clearTimeout(clearedTimer) })

const visibleTemplates = computed(() => [0,1,2].map(i => TEMPLATES[(templateOffset.value + i) % TEMPLATES.length]))
function rotateTemplates() { templateOffset.value = (templateOffset.value + 3) % TEMPLATES.length }

function renderTemplate(t) {
    const cast1 = props.selectedCast[0] ? props.selectedCast[0].split(' ')[0] : null
    const settingMatch = t.settingPrefs.find(p => props.settings.includes(p))
    const settingLabel = settingMatch ? SETTINGS_DATA.find(s => s.id === settingMatch)?.name.toLowerCase() ?? 'scene' : 'scene'
    let body = cast1 && t.body.oneCast ? t.body.oneCast.replace('{cast1}', cast1) : t.body.zeroCast
    return body.replace('{setting}', settingLabel)
}

function handleUseTemplate(t) {
    const body = renderTemplate(t)
    if (props.setup.trim().length > 0 && props.setup.trim() !== body.trim()) {
        pendingTemplate.value = t; return
    }
    emit('update:setup', body.slice(0, 280))
}
function confirmReplace() {
    if (!pendingTemplate.value) return
    emit('update:setup', renderTemplate(pendingTemplate.value).slice(0, 280))
    pendingTemplate.value = null
}

const filteredTags = computed(() =>
    tagSearch.value.trim()
        ? (props.allTags || []).filter(t => t.toLowerCase().includes(tagSearch.value.toLowerCase()) && !props.tags.includes(t)).slice(0, 8)
        : []
)
const exactTagMatch = computed(() =>
    tagSearch.value.trim()
        ? (props.allTags || []).some(t => t.toLowerCase() === tagSearch.value.trim().toLowerCase()) ||
        props.tags.some(t => t.toLowerCase() === tagSearch.value.trim().toLowerCase())
        : true
)

function addTag(tag) { if (props.tags.length < 10) { emit('toggleTag', tag); tagSearch.value = '' } }
function addCustomTag() {
    const val = tagSearch.value.trim().slice(0, 30)
    if (val && props.tags.length < 10 && !props.tags.some(t => t.toLowerCase() === val.toLowerCase())) emit('toggleTag', val)
    tagSearch.value = ''
}
function onTagEnter() {
    if (!tagSearch.value.trim()) return
    if (filteredTags.value.length > 0) addTag(filteredTags.value[0])
    else if (!exactTagMatch.value) addCustomTag()
}

function clearAllTags() {
    if (props.tags.length === 0) return
    clearedTags.value = [...props.tags]
    props.tags.forEach(t => emit('toggleTag', t))
    clearTimeout(clearedTimer)
    clearedTimer = setTimeout(() => { clearedTags.value = null }, 6000)
}
function handleUndoClear() {
    if (!clearedTags.value) return
    clearedTags.value.forEach(t => emit('toggleTag', t))
    clearedTags.value = null; clearTimeout(clearedTimer)
}

function handleNext() { if (!props.setup.trim()) { error.value = true } else { emit('next') } }

</script>

<style scoped>
.step-wrap { display:flex; flex-direction:column; height:100%; padding:12px 16px 0; }
.step-body { flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; }
.shrink-0 { flex-shrink:0; }
.step-header { margin-bottom:14px; }
.step-eyebrow { font-size:12px; color:rgba(77,148,255,0.8); font-weight:600; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:4px; }
.step-h1 { font-size:28px; font-weight:800; color:white; margin-bottom:4px; }
.step-lead { color:rgba(255,255,255,0.6); font-size:14px; }
.templates-block { margin-bottom:14px; }
.templates-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
.section-label { font-size:11px; font-weight:700; color:rgba(255,255,255,0.5); text-transform:uppercase; letter-spacing:0.1em; }
.shuffle-btn { display:flex; align-items:center; gap:4px; color:#4D94FF; font-size:12px; font-weight:500; background:none; border:none; cursor:pointer; min-height:32px; padding:0 6px; }
.template-pills { display:flex; gap:8px; overflow-x:auto; padding-bottom:4px; }
.template-pill { flex-shrink:0; padding:7px 14px; border-radius:999px; font-size:13px; border:1px solid rgba(255,255,255,0.1); background:rgba(255,255,255,0.05); color:rgba(255,255,255,0.8); cursor:pointer; min-height:36px; white-space:nowrap; }
.template-pill:hover { background:rgba(0,82,204,0.2); border-color:rgba(0,82,204,0.4); color:white; }
.replace-confirm { display:flex; align-items:center; gap:8px; margin-top:8px; padding:10px 12px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); border-radius:12px; }
.replace-text { flex:1; font-size:12px; color:rgba(255,255,255,0.7); line-height:1.4; }
.replace-yes { font-size:12px; font-weight:700; color:white; background:rgba(0,82,204,0.8); border:none; border-radius:8px; padding:5px 10px; cursor:pointer; min-height:30px; white-space:nowrap; }
.replace-no { font-size:12px; color:rgba(255,255,255,0.5); background:none; border:none; cursor:pointer; min-height:30px; padding:0 6px; }
.synopsis-textarea { min-height:120px; max-height:220px; width:100%; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); border-radius:16px; padding:14px; color:white; resize:none; outline:none; font-size:15px; line-height:1.6; font-family:inherit; transition:border-color 0.2s; }
.synopsis-textarea:focus { border-color:rgba(77,148,255,0.5); }
.synopsis-textarea::placeholder { color:rgba(255,255,255,0.5); }
.char-row { display:flex; justify-content:flex-end; margin-top:4px; }
.char-count { font-size:11px; font-variant-numeric:tabular-nums; }
.options-strip { display:flex; flex-wrap:wrap; gap:8px; margin-top:12px; padding-top:12px; border-top:1px solid rgba(255,255,255,0.1); }
.tag-count-pill { display:flex; align-items:center; gap:4px; background:rgba(0,82,204,0.2); border:1px solid rgba(0,82,204,0.4); border-radius:999px; padding:5px 6px 5px 10px; min-height:36px; }
.tag-count-btn { background:none; border:none; color:#4D94FF; font-size:12px; font-weight:500; cursor:pointer; display:flex; align-items:center; gap:4px; }
.tag-clear-btn { width:24px; height:24px; display:flex; align-items:center; justify-content:center; background:none; border:none; color:rgba(77,148,255,0.7); cursor:pointer; border-radius:50%; font-size:11px; }
.undo-btn { display:flex; align-items:center; gap:6px; background:rgba(255,255,255,0.1); border:1px solid rgba(255,255,255,0.25); border-radius:999px; padding:5px 12px; font-size:12px; color:rgba(255,255,255,0.8); cursor:pointer; min-height:36px; }
.add-tags-btn { display:flex; align-items:center; gap:6px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.2); border-radius:999px; padding:5px 14px; font-size:12px; color:rgba(255,255,255,0.6); cursor:pointer; min-height:36px; }
.add-tags-btn:disabled { opacity:0.4; }
.notes-btn { display:flex; align-items:center; gap:6px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.2); border-radius:999px; padding:5px 14px; font-size:12px; color:rgba(255,255,255,0.6); cursor:pointer; min-height:36px; }
.notes-active { background:rgba(0,82,204,0.1); border-color:rgba(0,82,204,0.3); color:#4D94FF; }
.step-footer { padding:12px 0 4px; display:flex; flex-direction:column; gap:6px; }
.error-msg { color:#f87171; font-size:13px; text-align:center; font-weight:500; }
.cta-btn { width:100%; background:#0052CC; color:white; font-weight:700; font-size:17px; padding:14px 0; border-radius:16px; border:none; cursor:pointer; }
/* Sheet */
.sheet-backdrop { position:fixed; inset:0; z-index:70; background:rgba(0,0,0,0.6); backdrop-filter:blur(4px); display:flex; align-items:flex-end; }
.bottom-sheet { width:100%; max-height:85vh; display:flex; flex-direction:column; background:#121212; border-top:1px solid rgba(255,255,255,0.1); border-radius:20px 20px 0 0; padding-bottom:max(env(safe-area-inset-bottom,0px),16px); }
.drag-handle { width:36px; height:4px; border-radius:2px; background:rgba(255,255,255,0.2); margin:10px auto 4px; flex-shrink:0; }
.sheet-header { display:flex; align-items:center; justify-content:space-between; padding:10px 20px 14px; flex-shrink:0; }
.sheet-title { font-size:15px; font-weight:700; color:white; }
.sheet-sub { font-size:11px; color:rgba(255,255,255,0.5); margin-top:2px; }
.sheet-done { background:none; border:none; color:#4D94FF; font-weight:700; font-size:13px; cursor:pointer; min-height:44px; padding:0 10px; }
.tag-chips { display:flex; flex-wrap:wrap; gap:6px; padding:0 20px 12px; flex-shrink:0; }
.tag-chip { display:flex; align-items:center; gap:4px; background:rgba(0,82,204,0.2); border:1px solid rgba(0,82,204,0.3); border-radius:999px; padding:3px 6px 3px 10px; }
.tag-chip-hash { font-size:11px; color:rgba(77,148,255,0.6); }
.tag-chip-text { font-size:12px; color:#4D94FF; }
.tag-chip-remove { background:none; border:none; color:#4D94FF; cursor:pointer; padding:4px; border-radius:50%; font-size:10px; }
.suggested-tags { padding:0 20px 12px; flex-shrink:0; }
.suggested-row { display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
.suggested-pill { display:flex; align-items:center; gap:4px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.15); border-radius:999px; padding:5px 10px; font-size:12px; color:rgba(255,255,255,0.7); cursor:pointer; min-height:30px; }
.suggested-pill:disabled { opacity:0.3; }
.tag-search-wrap { padding:0 20px 10px; flex-shrink:0; }
.tag-search-input { width:100%; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); border-radius:12px; padding:10px 14px; color:white; outline:none; font-size:16px !important; }
.tag-search-input:focus { border-color:rgba(77,148,255,0.5); }
.tag-search-input:disabled { opacity:0.5; }
.tag-results { flex:1; overflow-y:auto; padding:0 20px 12px; }
.tag-hint { font-size:12px; color:rgba(255,255,255,0.4); padding:12px 0; }
.tag-result-btn { width:100%; display:flex; align-items:center; gap:8px; padding:10px; font-size:13px; color:rgba(255,255,255,0.8); background:none; border:none; cursor:pointer; text-align:left; border-radius:8px; min-height:44px; }
.tag-result-btn:hover { background:rgba(255,255,255,0.08); }
.tag-custom-btn { width:100%; display:flex; align-items:center; gap:8px; padding:10px; font-size:13px; color:#4D94FF; background:none; border:none; cursor:pointer; text-align:left; border-radius:8px; border-top:1px solid rgba(255,255,255,0.05); margin-top:8px; min-height:44px; }
.tag-custom-btn:hover { background:rgba(0,82,204,0.1); }
.notes-body { padding:0 20px 16px; flex-shrink:0; }
.notes-textarea { width:100%; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); border-radius:16px; padding:14px; color:white; resize:none; outline:none; font-size:15px; line-height:1.6; font-family:inherit; }
.notes-textarea:focus { border-color:rgba(77,148,255,0.5); }
.notes-textarea::placeholder { color:rgba(255,255,255,0.5); }
.clear-notes { margin-top:10px; font-size:12px; color:rgba(248,113,113,0.7); background:none; border:none; cursor:pointer; min-height:32px; padding:0 4px; }

/* Transitions */
.fade-height-enter-active, .fade-height-leave-active { transition:opacity 0.2s, max-height 0.2s; overflow:hidden; max-height:80px; }
.fade-height-enter-from, .fade-height-leave-to { opacity:0; max-height:0; }
.fade-enter-active, .fade-leave-active { transition:opacity 0.2s; }
.fade-enter-from, .fade-leave-to { opacity:0; }
.backdrop-enter-active, .backdrop-leave-active { transition:opacity 0.25s; }
.backdrop-enter-from, .backdrop-leave-to { opacity:0; }
.sheet-up-enter-active, .sheet-up-leave-active { transition:transform 0.35s cubic-bezier(0.32,0.72,0,1); }
.sheet-up-enter-from, .sheet-up-leave-to { transform:translateY(100%); }
</style>
